import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import Raven from 'raven-js';
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CHANNEL_EVENT_MODEL_FINGERPRINT } from '../../../__generated__/com/hubspot/copilot/models/chat/ChannelEvent';
import { selectAblyChannelId } from '../../chat/selectors/chatv2Selectors';
import { chatV2Actions } from '../../chat/slices/chatv2';
import { useIsUngatedForChirpOverWs } from '../../gating/hooks/useIsUngatedForChirpOverWs';
import { AblyRealtimeClientContext } from '../context/AblyRealtimeClientProvider';
export const useCopilotV2PublishAblyMessage = ({
  hideUserMessage = false
}) => {
  const ablyRealtimeClient = useContext(AblyRealtimeClientContext);
  const ablyChannelId = useSelector(selectAblyChannelId);
  const dispatch = useDispatch();
  const isUngatedForChirpOverWs = useIsUngatedForChirpOverWs();

  /**
   * Publishes a message to the Ably channel
   * @param {MessageWithMetadata} payload - The message to publish
   * @returns {void}
   *
   * @description
   * This function publishes a message to the Ably channel
   */
  const publishAblyMessage = useCallback(payload => {
    if (!ablyChannelId) {
      return;
    }

    // customCopilotId is already attached to the thread, so we don't need to send it here
    const {
      context,
      messageTracking,
      message,
      type,
      interactiveInput
    } = payload;
    const callbackHandler = error => {
      // null indicates successful publish
      if (error === null) {
        // TODO - handle successful case, should there be a loading state?
      } else {
        consoleIfNotProd.error('Failure publishing message to Ably channel via publishAblyMessage: ', error);
        Raven.captureException(error, {
          extra: {
            component: 'useCopilotV2PublishAblyMessage',
            action: 'publishAblyMessage',
            context: 'Ably channel publish failed',
            hasChannelId: !!ablyChannelId,
            messageLength: (message === null || message === void 0 ? void 0 : message.length) || 0,
            isChirpOverWs: isUngatedForChirpOverWs
          }
        });
        dispatch(chatV2Actions.chatUserMessageSentException(payload));
      }
    };
    const currentAblyChannel = ablyRealtimeClient.channels.get(ablyChannelId);
    dispatch(chatV2Actions.chatUserMessageSentPending(Object.assign({}, payload, {
      hideUserMessage
    })));
    if (isUngatedForChirpOverWs) {
      const messageObj = {
        __typename: 'com.hubspot.copilot.models.chat.ChatRequest',
        context,
        interactiveInput,
        message,
        messageTracking,
        type
      };
      currentAblyChannel.publish('user_message', {
        chirpMessageJson: JSON.stringify(messageObj),
        chirpFingerprint: CHANNEL_EVENT_MODEL_FINGERPRINT
      }, callbackHandler);
    } else {
      // Legacy format
      currentAblyChannel.publish('user_message', {
        context,
        interactiveInput,
        message,
        messageTracking,
        type
      }, callbackHandler);
    }
  }, [ablyRealtimeClient, ablyChannelId, dispatch, hideUserMessage, isUngatedForChirpOverWs]);
  return publishAblyMessage;
};